import {
  type AccountCustomerPortal,
  type ArcualAccountProfile,
  getAccountService,
  type HttpError,
} from '@art-basel/artbasel-lib-platform';
import { cdn } from '@/utils/cdn';
import { handleHttpError } from '@/utils/errorHandlers';
import { defineStore } from 'pinia';
import { type Ref, ref } from 'vue';
import { platformHttpServiceConfiguration } from '@/services/PlatformHttpServiceConfiguration';
import type { AmplitudeFeatureFlagService } from '@/services/feature/providers/AmplitudeFeatureFlagService';

export const AccountStoreName = 'AccountStore';
export const useAccountStore = defineStore(AccountStoreName, () => {
  const account = ref<ArcualAccountProfile | undefined>();

  const accounts = ref<Record<string, ArcualAccountProfile>>({});

  const defaultAddress = {
    country: '',
    street: '',
    secondAddressLine: '',
    houseNumber: '',
    flat: '',
    zip: '',
    city: '',
    state: '',
  };

  const defaultExternalLinks = {};

  const setAccount = (accountProfile?: ArcualAccountProfile) => {
    account.value = accountProfile;

    if (accountProfile?.id) {
      accounts.value[accountProfile.id] = accountProfile;

      const featureFlagProvider = platformHttpServiceConfiguration.featureFlagProvider();

      if (featureFlagProvider) {
        // TODO: refactor to platform lib to avoid casting
        (featureFlagProvider as AmplitudeFeatureFlagService).setAccount(accountProfile.id);
      }
    }
  };

  const loadAccountProfile = async () => {
    try {
      const result = await getAccountService().getAccountProfile();

      if (!result.address) {
        result.address = defaultAddress;
      }
      if (!result.externalLinks) {
        result.externalLinks = defaultExternalLinks;
      }
      setAccount(result);
    } catch (e) {
      handleHttpError(e);
    }
  };

  const clearAccount = () => setAccount(undefined);

  const currentAccount = (): Ref<ArcualAccountProfile | undefined> => account;

  const getAccountById = async (id: string) => {
    let result = accounts.value[id];
    if (result) {
      return result;
    }

    try {
      result = await getAccountService().getAccountProfileById(id);
      if (!result.address) {
        result.address = defaultAddress;
      }

      accounts.value[result.id] = result;

      return result;
    } catch (e) {
      handleHttpError(e);
      throw e;
    }
  };

  const updateAccountProfile = async (accountProfile: ArcualAccountProfile) => {
    if (!accountProfile.id) {
      throw new Error('Account ID is required for update');
    }

    try {
      // Exclude imageUrl because it is already handled in the upload methods
      const profileToUpdate = { ...accountProfile };
      delete profileToUpdate.imageUrl;

      const result = await getAccountService().updateAccountProfile(accountProfile.id, profileToUpdate);
      setAccount(result);
      return result;
    } catch (e) {
      handleHttpError(e);
      return Promise.reject(e);
    }
  };

  const updateAccountPicture = async (picture: File) => {
    if (account.value?.id) {
      await getAccountService()
        .uploadAccountPicture(account.value.id, picture)
        .then((response: string) => {
          if (account.value) {
            account.value.imageUrl = cdn(response);
            if (accounts.value[account.value.id]) {
              accounts.value[account.value.id].imageUrl = cdn(response);
            }
          }
        })
        .catch(handleHttpError);
    }
  };

  const updateAccountSignature = async (picture: File) => {
    if (account.value?.id) {
      return getAccountService()
        .uploadAccountSignature(account.value.id, picture)
        .then((response: string) => {
          if (account.value) {
            account.value.signatureUrl = cdn(response);
            if (accounts.value[account.value.id]) {
              accounts.value[account.value.id].signatureUrl = cdn(response);
            }
          }
        })
        .catch(handleHttpError);
    }
  };

  const getAccountByEmail = async (email: string): Promise<ArcualAccountProfile | null> => {
    try {
      return await getAccountService().getAccountProfile(email);
    } catch (error) {
      // do not show an error message in UI unless it's not a 404
      if ((error as HttpError).status != 404) {
        handleHttpError(error);
        return Promise.reject(error);
      }
      return null;
    }
  };

  const updateAccountLogo = async (picture: File) => {
    if (account.value?.id) {
      return getAccountService()
        .uploadAccountLogo(account.value.id, picture)
        .then((response: string) => {
          if (account.value) {
            account.value.logoUrl = cdn(response);
            if (accounts.value[account.value.id]) {
              accounts.value[account.value.id].logoUrl = cdn(response);
            }
          }
        })
        .catch(handleHttpError);
    }
  };

  const acceptTerms = async (accountProfile: ArcualAccountProfile) => {
    try {
      const result = await getAccountService().acceptTerms(accountProfile);
      setAccount(result);
      return result;
    } catch (e) {
      handleHttpError(e);
    }
  };

  const reset = () => {
    setAccount(undefined);
    accounts.value = {};
  };

  const postAccountCustomerPortal = async (returnUrl: string): Promise<AccountCustomerPortal | null> => {
    try {
      const result = await getAccountService().postAccountCustomerPortal(returnUrl);
      return result;
    } catch (error) {
      if ((error as HttpError).status != 404) {
        handleHttpError(error);
        return Promise.reject(error);
      }
      return null;
    }
  };

  return {
    currentAccount,
    clearAccount,
    loadAccountProfile,
    updateAccountProfile,
    getAccountById,
    getAccountByEmail,
    updateAccountPicture,
    updateAccountSignature,
    updateAccountLogo,
    acceptTerms,
    reset,
    postAccountCustomerPortal,
    setAccount,
    accounts,
  };
});
